import { useState, useMemo, useEffect } from "react";
import api, { API_BASE_URL, studioApi } from "../api";
import { useApi } from "../hooks";
import useClipboard from "../utils";
import Loader from "../component/editor/loader";
import VideoPDF, { IPdfs } from "./videoPDF";
import { message } from "antd";

export interface IVideos {
  id: number;
  name: string;
  course_id: number;
  center_id: string;
  studio_id: number | null;
  chat_id: number | null;
  lesson_id: number;
  video_url: string;
  hd_video_url: string;
  duration: number;
  order: number;
  type: string;
  mp4s: string;
  download_url: string;
  pdfs: IPdfs[];
  is_live: number;
  end_at: string;
  published_at: string;
  enabled: number;
  is_feedback: number;
  thumb: string;
  is_free: number;
  is_live_chat: number;
  is_comment: number;
  teacher_id: number;
  is_demo: number;
  created_at: string;
  updated_at: string;
  actual_start_at: string | null;
  video_detail?: {
    actual_start_time: string | null;
    actual_end_time: string | null;
  };
}

interface IProps {
  vId: number;
  streamKey?: string;
  status?: boolean;
}

const StudioVideoDetails = ({ vId, streamKey, status }: IProps) => {
  const [loading, setLoading] = useState(false);
  const [actualStartTime, setActualStartTime] = useState("");
  const [actualEndTime, setActualEndTime] = useState("");
  const [timingLoading, setTimingLoading] = useState(false);
  const { data, load, isLoading } = useApi<IVideos>(
    API_BASE_URL + `videos/v2/${vId!}`
  );
  const { copy } = useClipboard();
  const [messageApi, context] = message.useMessage();

  const token = localStorage.getItem("AUTH_TOKEN");

  useMemo(() => {
    if (!vId) return;
    load();
  }, [vId]);

  const renderUrls = [
    {
      label: "Video URL",
      url: data?.video_url,
      message: "BackupUrl Copied",
    },
    {
      label: "HD Video URL",
      url: data?.hd_video_url,
      message: "Download Url Copied",
    },
    {
      label: "Download URL",
      url: data?.download_url,
      message: "Recorded URL Copied",
    },
    {
      label: "MP4 URL",
      url: data?.mp4s ? JSON.stringify(data.mp4s) : "",
      message: "Cdn Url Copied",
    },
  ];

  const handleSyncAdmin = async () => {
    try {
      setLoading(true);
      const payload = { stream_key: streamKey! };
      const response = await api.post(`videos/update-admin`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      messageApi.success(response.data.message);
      load();
    } catch (error: any) {
      messageApi.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTiming = async () => {
    try {
      setTimingLoading(true);
      await studioApi.post(`videos/${vId}/update-timing`, {
        actual_start_time: actualStartTime,
        actual_end_time: actualEndTime,
      });
      messageApi.success("Timing updated successfully");
      load();
    } catch (error: any) {
      messageApi.error(
        error?.response?.data?.message || "Failed to update timing"
      );
    } finally {
      setTimingLoading(false);
    }
  };



  useEffect(() => {
    if (!data?.video_detail) return;
  
    const formatForInput = (date?: string | null) => {
      if (!date) return "";
      return date.replace(" ", "T").slice(0, 16);
    };
  
    setActualStartTime(
      formatForInput(data.video_detail.actual_start_time)
    );
  
    setActualEndTime(
      formatForInput(data.video_detail.actual_end_time)
    );
  }, [data]);

  if (isLoading) return <Loader />;
  return (
    <div className="w-full flex flex-col gap-4 mt-1">
      {context}
      <div className="w-full border border-slate-200 bg-white shadow-sm overflow-hidden text-sm text-slate-700">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/70 px-4 py-3">
          <span className="font-bold text-slate-800 text-base">
            Admin Details
          </span>
          <button
            disabled={loading}
            onClick={handleSyncAdmin}
            className="px-3 py-1.5 text-xs font-semibold tracking-wide text-white
             bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400
              rounded shadow-sm transition cursor-pointer"
          >
            {loading ? "Syncing..." : "Sync with Admin"}
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {renderUrls.map((item, idx) => (
            <div
              key={idx}
              className=" gap-3 grid grid-cols-6 items-center px-4 py-2.5"
            >
              <div className="text-blue-900 font-semibold col-span-1 w-60">
                {item.label}
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="font-mono text-xs text-slate-600 truncate">
                  {item.url || "N/A"}
                </span>
                {item.url && (
                  <button
                    onClick={() => copy(item.url, item.message)}
                    className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
                  >
                    Copy
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 flex justify-between">
            <h3 className="font-bold text-slate-800 text-base">
              Actual Timing
            </h3>
            <button
              onClick={handleUpdateTiming}
              disabled={timingLoading}
              className="px-3 py-1.5 text-xs font-semibold tracking-wide text-white
             bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400
              rounded shadow-sm transition cursor-pointer"
            >
              {timingLoading ? "Updating..." : "Update Timing"}
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Actual Start Time
              </label>
              <input
                type="datetime-local"
                value={actualStartTime}
                onChange={(e) => setActualStartTime(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Actual End Time
              </label>
              <input
                type="datetime-local"
                value={actualEndTime}
                onChange={(e) => setActualEndTime(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2"
              />
            </div>
          </div>
        </div>
        <div className="w-full">
          <VideoPDF
            pdfs={data?.pdfs}
            stream_key={streamKey}
            load={load}
            title={data?.name}
            videoIds={[vId]}
          />
        </div>
      </div>
    </div>
  );
};

export default StudioVideoDetails;
